import Logo from "@/assets/logo.svg?react";
import { LoginButton } from "@/base";
import { Link, Spinner } from "@heroui/react";
import { ApplicantDocumentStatus } from "@sr-partner-portal/common";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { ApplicantNotFound } from "../components/applicant-not-found";
import { SharedApplicant } from "../components/shared-applicant";
import { Api } from "../api";

const SharedApplicantPage = () => {
    const { token } = useParams();
    const [refetchCount, setRefetchCount] = useState(0);

    const sharedApplicantQuery = useQuery({
        queryKey: ["sharedApplicant", token],

        queryFn: () => Api.getByShareLinkToken(token),

        enabled: !!token,
    });

    const applicantDocumentsQuery = useQuery({
        queryKey: ["applicantDocumentsByToken", token],
        queryFn: () => Api.getDocumentsByShareLinkToken(token),
        enabled: !!token,
        refetchInterval: (query) => {
            const currentData = query.state.data;
            const needsScan = currentData?.some(
                (doc) => doc.status === ApplicantDocumentStatus.NEEDS_SCAN,
            );

            // stop refetching if no documents need scan
            if (currentData && !needsScan) {
                setRefetchCount(0);
                return false;
            }

            if (refetchCount === 0) {
                return 1000 * 6; // first refetch after 6 seconds
            }

            if (refetchCount >= 26) {
                return false;
            }

            // After initial delay, poll every 2 seconds
            return 1000 * 2;
        },
    });

    // Refetch until no documents need scan anymore
    useEffect(() => {
        // Ensure we don't run on the very first render before any fetch happens
        if (
            applicantDocumentsQuery.dataUpdatedAt === 0 &&
            applicantDocumentsQuery.errorUpdatedAt === 0
        ) {
            return;
        }

        // Check if we should reset (equivalent to the logic you had inside refetchInterval)
        const needsScan = applicantDocumentsQuery.data?.some(
            (doc) => doc.status === ApplicantDocumentStatus.NEEDS_SCAN,
        );

        if (applicantDocumentsQuery.data && !needsScan) {
            setRefetchCount(0);
        } else {
            setRefetchCount((c) => c + 1);
        }
    }, [
        applicantDocumentsQuery.dataUpdatedAt,
        applicantDocumentsQuery.errorUpdatedAt,
        applicantDocumentsQuery.data,
    ]);

    const applicant = sharedApplicantQuery.data;

    useEffect(() => {
        if (applicant?.name) {
            document.title = applicant.name;
        }
    }, [applicant?.name]);

    const isLoading =
        sharedApplicantQuery.isPending || applicantDocumentsQuery.isLoading;

    return (
        <div className="flex h-full w-full flex-col items-center px-4 md:px-8">
            <header className="flex w-full items-center justify-between py-4">
                <Logo className="h-4 flex-none md:h-6" />
                <LoginButton
                    primary={true}
                    className="h-8 rounded-small md:h-10 md:rounded-medium"
                />
            </header>
            {isLoading ? (
                <div className="flex h-full items-center justify-center">
                    <Spinner />
                </div>
            ) : applicant ? (
                <SharedApplicant
                    applicant={applicant}
                    documents={applicantDocumentsQuery.data || []}
                />
            ) : (
                <ApplicantNotFound />
            )}

            <footer className="flex w-full flex-col-reverse items-center justify-between gap-4 py-6 md:flex-row md:py-8">
                <Logo className="h-4 flex-none" />
                <div className="flex items-center gap-4">
                    <Link
                        color="foreground"
                        href="https://www.stahlhart-recruiting.de/datenschutzerklarung"
                        target="_blank"
                        underline="hover"
                    >
                        Datenschutzerklärung
                    </Link>
                    <Link
                        color="foreground"
                        href="https://www.stahlhart-recruiting.de/agb"
                        target="_blank"
                        underline="hover"
                    >
                        AGB
                    </Link>
                </div>
            </footer>
        </div>
    );
};

export { SharedApplicantPage };
